// AuthProvider.js
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { MsalProvider, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import {
  ACCESS_TOKEN_LOCAL_STORAGE_KEY,
  azureClientId,
  loginRequest,
  msalInstance,
} from './authConfig';
import API from '../../services/api';
import { QUERY_KEYS } from '../../config/queryKey';
import { useQuery } from '@tanstack/react-query';
// Initialize MSAL instance

const AuthContext = createContext(null);

// Export the provider component
const AuthProvider = ({ children }) => {
  return (
    <MsalProvider instance={msalInstance}>
      <AuthProviderInternal>{children}</AuthProviderInternal>
    </MsalProvider>
  );
};

const AuthProviderInternal = ({ children }) => {
  const { accounts, inProgress, instance } = useMsal();
  const azureUser = accounts && accounts.length > 0 ? accounts[0] : null;

  const [initialized, setInitialized] = useState(false);
  const [token, setToken] = useState(null);

  const { data: user, refetch } = useQuery({
    queryKey: [QUERY_KEYS.AUTH.ME, token],
    queryFn: () => API.auth.getMe(),
    enabled: !!token,
  });

  const initializeUser = useCallback(async () => {
    if (inProgress !== InteractionStatus.None) return;
    try {
      if (azureUser) {
        const token = await instance.acquireTokenSilent({
          account: azureUser,
          scopes: [`api://${azureClientId}/access_as_user`],
        });
        if (token && token.accessToken) {
          // save token to local storage
          localStorage.setItem(
            ACCESS_TOKEN_LOCAL_STORAGE_KEY,
            token.accessToken,
          );
          setToken(token.accessToken);
        }
      } else {
        localStorage.removeItem(ACCESS_TOKEN_LOCAL_STORAGE_KEY);
        setToken(null);
      }
    } catch (error) {
      console.error(error);
      localStorage.removeItem(ACCESS_TOKEN_LOCAL_STORAGE_KEY);
    } finally {
      setInitialized(true);
    }
  }, [azureUser, instance, inProgress]);

  const handleLogin = useCallback(() => {
    instance.loginPopup(loginRequest).catch((e) => {
      console.error(e);
    });
  }, [instance]);

  const handleLogout = useCallback(() => {
    // Clear the cache for current application only
    instance.clearCache();
    // Remove local storage token
    localStorage.removeItem(ACCESS_TOKEN_LOCAL_STORAGE_KEY);
    // Reset account state
    setToken(null);
    refetch();
  }, [instance, refetch]);

  useEffect(() => {
    initializeUser();
  }, [initializeUser]);

  return (
    <AuthContext.Provider
      value={{
        initialized,
        user,
        isLoading: inProgress !== InteractionStatus.None,
        handleLogin,
        handleLogout,
        refetchUser: refetch,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within a AuthProvider');
  }
  return context;
};

export default AuthProvider;
